import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useSocket } from '../../SocketContext';

export const useEmails = (authenticated, user, selectedAccount) => {
  const [emailMessages, setEmailMessages] = useState([]);
  const socket = useSocket();
  const loadingRef = useRef(false);
  const lastLoadTimeRef = useRef(0);
  const loadThrottleMs = 30000; // Минимум 30 секунд между запросами

  const loadEmailMessages = useCallback(() => {
    const now = Date.now();
    
    // Проверяем, не загружаются ли уже сообщения и не был ли запрос недавно
    if (loadingRef.current || (now - lastLoadTimeRef.current < loadThrottleMs)) {
      console.log('🛑 [useEmails] Skipping load - already loading or throttled');
      return;
    }
    
    if (authenticated && user?._id && selectedAccount) {
      console.log('🔄 [useEmails] Loading email messages');
      loadingRef.current = true;
      
      axios
        .get('/api/mail/messages', {
          params: { accountId: selectedAccount._id, folder: 'Inbox' },
          withCredentials: true,
          timeout: 15000 // Устанавливаем таймаут в 15 секунд
        })
        .then((res) => {
          setEmailMessages(res.data || []);
          lastLoadTimeRef.current = Date.now();
        })
        .catch((err) => {
          console.error('Failed to fetch email messages:', err);
          // Не обновляем lastLoadTime при ошибке, чтобы можно было повторить попытку
        })
        .finally(() => {
          loadingRef.current = false;
        });
    }
  }, [authenticated, user, selectedAccount, loadThrottleMs]);

  const markEmailAsRead = (id) => {
    setEmailMessages((prev) =>
      prev.map((msg) => (msg._id === id ? { ...msg, isRead: true } : msg))
    );
  };

  // Listen for email refresh events from EmailNotificationPush
  useEffect(() => {
    const handleRefreshEmails = () => {
      console.log('🔄 [useEmails] Received refreshEmails event, reloading emails');
      loadEmailMessages();
    };

    window.addEventListener('refreshEmails', handleRefreshEmails);
    
    return () => {
      window.removeEventListener('refreshEmails', handleRefreshEmails);
    };
  }, [loadEmailMessages]);

  // Listen for email notifications directly
  useEffect(() => {
    if (!socket || !user?._id) return;
    
    const handleNewEmail = (notification) => {
      if (notification.type === 'newEmail' && 
          notification.user && 
          user._id && 
          notification.user.toString() === user._id.toString()) {
        
        console.log('📨 [useEmails] Received new email notification, updating counter');
        
        // Update the emailMessages state when a new email arrives
        // We can either add the new email to the list if it has all required fields
        // or just trigger a reload to get the updated list from the server
        if (notification.email && notification.email.from && notification.email.subject) {
          // Create a temporary email object from the notification
          const newEmail = {
            _id: notification.email._id || `temp-${Date.now()}`,
            from: notification.email.from,
            subject: notification.email.subject,
            text: notification.email.text || '',
            date: notification.email.date || new Date().toISOString(),
            isRead: false
          };
          
          // Add to the beginning of the list
          setEmailMessages(prev => [newEmail, ...prev]);
        } else {
          // Проверяем можно ли загрузить (прошло ли достаточно времени)
          const now = Date.now();
          if (now - lastLoadTimeRef.current >= loadThrottleMs) {
            loadEmailMessages();
          } else {
            console.log('🛑 [useEmails] Throttling load after notification');
          }
        }
      }
    };
    
    socket.on('notificationCreated', handleNewEmail);
    
    return () => {
      socket.off('notificationCreated', handleNewEmail);
    };
  }, [socket, user, loadEmailMessages, loadThrottleMs]);

  useEffect(() => {
    // Загружаем только при изменении аккаунта
    if (selectedAccount && selectedAccount._id) {
      loadEmailMessages();
    }
  }, [selectedAccount, loadEmailMessages]);

  return { emailMessages, loadEmailMessages, markEmailAsRead };
};